import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((type, title, message, duration = 5000) => {
    const id = Date.now() + Math.random();
    const notification = { id, type, title, message, duration };

    setNotifications(prev => [...prev, notification]);

    // Auto remove from local state
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, duration);

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    success: (title, message, duration) => addNotification('success', title, message, duration),
    error: (title, message, duration) => addNotification('error', title, message, duration),
    warning: (title, message, duration) => addNotification('warning', title, message, duration),
    info: (title, message, duration) => addNotification('info', title, message, duration),
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
        {notifications.map((n) => {
          const tone =
            n.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : n.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : n.type === 'warning'
                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-blue-50 border-blue-200 text-blue-800';
          return (
            <div
              key={n.id}
              className={`max-w-sm w-[360px] shadow-lg border rounded-xl p-3 ${tone}`}
              role="status"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  {n.title ? <div className="font-semibold text-sm">{n.title}</div> : null}
                  <div className="text-sm">{n.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => removeNotification(n.id)}
                  className="text-xs opacity-70 hover:opacity-100"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </NotificationContext.Provider>
  );
};